import clsx from 'clsx';
import { motion } from 'motion/react';
import * as React from 'react';
import { useFocusRing } from 'react-aria';

import { subtleSpring } from '../animations/motionPresets';
import type { BadgeProps } from '../Badge';
import { AtomLink, Button, ButtonIcon, type ButtonProps } from '../Button';
import { TextSkeleton } from '../Skeleton';
import cssStyles from './Tabs-spine.module.css';

// https://www.figma.com/file/aZsMhSIQp2xgRIDWpvRUqQ?node-id=5232:5295&mode=design#646379313
// When migrating tabs from react-uikit to spine, we should not try and have this component
// accommodate "vertical tabs" - rather those should be considered a separate menu component.

export interface TabsProps {
    scrollable?: boolean;
    orientation?: React.AriaAttributes['aria-orientation'];
    children: React.ReactNode;

    /**
     * **Can be used to optionally style the tabs container**
     *
     * - Supply one or more classnames (`text-ui-standout`, `text-danger`, ...)
     * - You can also use generic [Tailwind classes](https://tailwindcss.com/docs/utility-first) (`ml-4`, `blur`, ...) if they’re made available in our [stylesheet](https://github.com/GitbookIO/gitbook-x/blob/main/packages/spine-css/tailwind.css).
     * - Separate multiple classnames by a space.
     */
    className?: string;

    /**
     * Whether to add a gap between tabs or justify between them.
     */
    gap?: boolean;
}

export interface TabProps {
    /**
     * **Whether the tab is the currently selected one in the set of tabs.**
     *
     * - In its active state, Tabs gain an underline and primary colour.
     * - In addition to the tab’s (in)active state, tabs inherit states from Button. These button states apply regardless of the tab’s state.
     */
    active?: boolean;

    // Properties inherited from [Button](https://storybook.gitbook.dev/?path=/docs/components-button-button--docs)
    testID?: string;
    disabled?: boolean;
    badge?: BadgeProps;
    trailingIcon?: ButtonProps['trailingIcon'];
    leadingIcon?: ButtonProps['leadingIcon'];
    title?: ButtonProps['title'];
    onPress?: ButtonProps['onPress'];
    href?: ButtonProps['href'];
    label: string;
    iconOnly?: ButtonProps['iconOnly'];
    className?: string;
}

const TabsContext = React.createContext<{
    id: string;
    orientation: TabsProps['orientation'];
    scrollable: TabsProps['scrollable'];
}>({
    id: '',
    orientation: 'horizontal',
    scrollable: false,
});

/**
 * Tabs contain multiple Tab elements. Only one Tab can be selected at a time. You can have as many Tab children as needed.
 */
export function Tabs({ ref, ...props }: TabsProps & { ref?: React.Ref<HTMLDivElement> }) {
    const {
        orientation = 'horizontal',
        scrollable = false,
        children,
        className,
        gap = true,
    } = props;

    const id = React.useId();

    const value = React.useMemo(
        () => ({ id, orientation, scrollable }),
        [id, orientation, scrollable]
    );

    return (
        <TabsContext.Provider value={value}>
            {orientation === 'vertical' ? (
                <div
                    ref={ref}
                    className={clsx(
                        'flex h-full w-full flex-col',
                        scrollable &&
                            'scrollbar snap-y snap-mandatory flex-nowrap overflow-auto pr-3 [scrollbar-gutter:stable] *:snap-start',
                        gap ? 'gap-1' : 'justify-between',
                        className
                    )}
                    role="tablist"
                >
                    {children}
                </div>
            ) : (
                <div
                    ref={ref}
                    className={clsx(
                        'flex w-full items-end',
                        scrollable
                            ? 'scrollbar snap-x snap-mandatory flex-nowrap overflow-auto [scrollbar-gutter:stable] *:snap-start'
                            : 'flex-wrap',
                        gap ? 'gap-2' : 'justify-between',
                        className
                    )}
                    role="tablist"
                >
                    {children}
                </div>
            )}
        </TabsContext.Provider>
    );
}

/**
 * A Tab is a menu element that can be selected.
 *
 * Tab uses Button under the hood, which gives Tab the same states and interaction patterns as Button. Refer to [Button](https://storybook.gitbook.dev/?path=/docs/components-button-button--docs) for properties and documentation.
 */
export function Tab(props: TabProps) {
    const {
        active = false,
        className,
        disabled = false,
        iconOnly,
        leadingIcon,
        trailingIcon,
        ...linkProps
    } = props;
    const { id, orientation, scrollable } = React.useContext(TabsContext);
    const { isFocusVisible, focusProps } = useFocusRing();

    const tabRef = React.useRef<HTMLDivElement>(null);
    // Smooth-scroll only once the tab has already been positioned. On the first run we jump
    // instantly so the container lands at a deterministic scroll offset immediately, rather
    // than animating from 0 on mount (which reads as a stray auto-scroll and races screenshots).
    const hasScrolledRef = React.useRef(false);
    React.useEffect(() => {
        if (!scrollable) {
            return;
        }
        if (active && tabRef.current) {
            tabRef.current.scrollIntoView({
                behavior: hasScrolledRef.current ? 'smooth' : 'instant',
                ...(orientation === 'vertical'
                    ? {
                          inline: 'nearest',
                          block: 'center',
                      }
                    : {
                          inline: 'center',
                          block: 'nearest',
                      }),
            });
            hasScrolledRef.current = true;
        }
    }, [active, orientation, scrollable]);

    if (orientation === 'vertical') {
        const leadingIconSlot = leadingIcon ? (
            <ButtonIcon className="text-muted" icon={leadingIcon} />
        ) : null;

        const trailingIconSlot = trailingIcon ? (
            <ButtonIcon className="text-muted" icon={trailingIcon} />
        ) : null;
        return (
            <div className={clsx('relative flex', className)} ref={tabRef}>
                <AtomLink
                    role="tab"
                    {...linkProps}
                    className={clsx(
                        'flex w-full items-center gap-2 rounded-sm px-3 py-2 text-ui-base',
                        disabled ? 'pointer-events-none text-light' : 'text-base',
                        active
                            ? 'cursor-default bg-(--neutral-4)'
                            : 'bg-transparent hover:bg-(--neutral-3)',
                        cssStyles.focusable,
                        isFocusVisible && cssStyles.focusRing
                    )}
                    aria-orientation={orientation}
                    disabled={disabled}
                    {...focusProps}
                >
                    {leadingIconSlot}
                    {props.label}
                    {trailingIconSlot}
                </AtomLink>
                {/* TO-DO: add similar animation on tab active change than on horizontal tabs */}
            </div>
        );
    }

    return (
        <div
            className={clsx('relative flex', scrollable && 'max-w-[20ch] shrink-0', className)}
            ref={tabRef}
        >
            <Button
                role="tab"
                {...props}
                kind="blank"
                className={clsx(
                    'w-full px-1 py-2',
                    // Add distance between the animating underline and the button so
                    // hover states look correct and dont touch the underline below.
                    'mb-0.5',
                    cssStyles.button,
                    cssStyles.tabButton,
                    active ? cssStyles.active : null
                )}
                labelStyle={active ? 'text-ui-standout' : 'text-ui-action'}
                disabled={disabled}
                iconOnly={iconOnly}
            />

            {active ? (
                // The tab’s underline uses a motion.div with a layoutId shared by the Tabs container.
                // This makes it animate from one tab to the next when switching tabs.
                <motion.div
                    layoutId={id}
                    className={clsx(
                        'absolute bottom-0 left-0 flex w-full cursor-grabbing',
                        cssStyles.animatedLine
                    )}
                    style={{
                        originY: '0px',
                    }}
                    transition={subtleSpring}
                />
            ) : null}
        </div>
    );
}

/** Skeleton for the Tab component loading state */
export function TabSkeleton(props: { className?: string; label: string }) {
    return (
        <TextSkeleton className={clsx('w-full px-3 py-2 text-ui-base', props.className)}>
            {props.label}
        </TextSkeleton>
    );
}

/**
 * A heading for a set of vertical tabs.
 */
export function TabsHeading(props: { className?: string; children: React.ReactNode }) {
    return (
        <div className={clsx('px-1 text-ui-small text-muted', props.className)}>
            {props.children}
        </div>
    );
}

/** Skeleton for the TabsHeading component loading state */
export function TabsHeadingSkeleton(props: { className?: string; children: React.ReactNode }) {
    return (
        <TextSkeleton className={clsx('px-1 text-ui-small', props.className)}>
            {props.children}
        </TextSkeleton>
    );
}
